import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Eye, EyeSlash, Trash, PencilSimple, UploadSimple, Download, MagnifyingGlass, Cake, FilePdf } from "@phosphor-icons/react";
import { humanizeAxiosError } from "@/lib/api";

function isBirthdayToday(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const t = new Date();
  return d.getUTCMonth() === t.getMonth() && d.getUTCDate() === t.getDate();
}

export default function Personnel() {
  const { user, refresh } = useAuth();
  const { t, lang } = useI18n();
  const [list, setList] = useState([]);
  const [showSalary, setShowSalary] = useState(false);
  const [edit, setEdit] = useState(null);
  const [bulkSalaryOpen, setBulkSalaryOpen] = useState(false);
  const [bulkSalaryFile, setBulkSalaryFile] = useState(null);
  const [bulkSalaryResult, setBulkSalaryResult] = useState(null);
  const [uploadingSalary, setUploadingSalary] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [depReason, setDepReason] = useState("");
  const [depDate, setDepDate] = useState(new Date().toISOString().slice(0, 10));
  const [depNote, setDepNote] = useState("");
  const [deleting, setDeleting] = useState(false);
  const canEdit = ["admin", "hr"].includes(user?.role);
  // Maaş/IBAN/bordro yetkileri GERÇEK role göre — yetki devri bunları ne verir ne alır.
  const realRole = user?.base_role || user?.role;
  const canBulkSalary = ["admin", "hr", "gm"].includes(realRole);
  const canDownloadAllPayroll = ["admin", "hr", "gm"].includes(realRole);
  const canExportXlsx = ["admin", "hr", "gm"].includes(realRole);
  const canDelete = ["admin", "hr", "manager"].includes(user?.role);

  // --- Gelişmiş arama & filtreleme ---
  const [q, setQ] = useState("");
  const [filterDept, setFilterDept] = useState("all");
  const [hireFrom, setHireFrom] = useState("");
  const [hireTo, setHireTo] = useState("");

  const load = useCallback(() => api.get("/personnel").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);

  // Departman listesi — filtrelemek için mevcut personelden çıkar
  const departments = useMemo(() => {
    const s = new Set();
    list.forEach((p) => p.department && s.add(p.department));
    return [...s].sort((a, b) => a.localeCompare(b, "tr"));
  }, [list]);

  // Filtrelenmiş liste
  const filteredList = useMemo(() => {
    const query = (q || "").toLocaleLowerCase("tr");
    return list.filter((p) => {
      if (filterDept !== "all" && p.department !== filterDept) return false;
      if (hireFrom && p.hire_date && p.hire_date.slice(0, 10) < hireFrom) return false;
      if (hireTo && p.hire_date && p.hire_date.slice(0, 10) > hireTo) return false;
      if (!query) return true;
      const hay = [
        `${p.first_name} ${p.last_name}`,
        p.email, p.phone, p.department, p.position, p.tc_kimlik,
      ].filter(Boolean).join(" ").toLocaleLowerCase("tr");
      return hay.includes(query);
    });
  }, [list, q, filterDept, hireFrom, hireTo]);

  const del = (p) => {
    setDeleteTarget(p);
    setDepReason("");
    setDepDate(new Date().toISOString().slice(0, 10));
    setDepNote("");
  };

  const confirmDelete = async () => {
    if (!depReason) { toast.error("Lütfen ayrılma nedenini seçin"); return; }
    setDeleting(true);
    try {
      await api.delete(`/personnel/${deleteTarget.id}`, {
        data: { departure_reason: depReason, departure_date: depDate, note: depNote },
      });
      toast.success("Personel silindi ve ayrılma kaydı oluşturuldu");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    } finally {
      setDeleting(false);
    }
  };

  const save = async () => {
    const { id, ...rest } = edit;
    // number fields
    if (rest.salary !== undefined) rest.salary = Number(rest.salary);
    await api.patch(`/personnel/${id}`, rest);
    toast.success("Güncellendi");
    setEdit(null);
    load();
    refresh();
  };

  const doBulkSalaryUpload = async () => {
    if (!bulkSalaryFile) return toast.error("Excel dosyası seçin");
    setUploadingSalary(true);
    try {
      const fd = new FormData();
      fd.append("file", bulkSalaryFile);
      const r = await api.post("/personnel/bulk-salary-update", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkSalaryResult(r.data);
      toast.success(`${r.data.updated} personelin maaşı güncellendi`);
      load();
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    } finally {
      setUploadingSalary(false);
    }
  };

  const downloadAllPayrolls = async (p) => {
    try {
      toast.loading("Bordrolar hazırlanıyor…", { id: "dl-" + p.id });
      const r = await api.get(`/payroll/user/${p.id}/download-all`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bordrolar_${p.first_name}_${p.last_name}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("İndiriliyor", { id: "dl-" + p.id });
    } catch (e) {
      toast.error(humanizeAxiosError(e) || "İndirilecek bordro bulunamadı", { id: "dl-" + p.id });
    }
  };

  const downloadAllPayrollsPdf = async (p) => {
    try {
      toast.loading("İmzalı bordrolar PDF hazırlanıyor…", { id: "dlpdf-" + p.id });
      const r = await api.get(`/payroll/user/${p.id}/download-all-pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `imzali_bordrolar_${p.first_name}_${p.last_name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("İndiriliyor", { id: "dlpdf-" + p.id });
    } catch (e) {
      toast.error(humanizeAxiosError(e) || "İndirilecek imzalı bordro bulunamadı", { id: "dlpdf-" + p.id });
    }
  };

  const exportPersonnelXlsx = async () => {
    try {
      toast.loading("Excel hazırlanıyor…", { id: "personnel-xlsx" });
      const r = await api.get("/personnel/export-xlsx", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `personel_listesi_${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel indirildi", { id: "personnel-xlsx" });
    } catch (e) {
      toast.error(humanizeAxiosError(e) || "İndirme başarısız", { id: "personnel-xlsx" });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("personnel.list_title")}</div>
          <h1 className="font-display text-3xl font-bold mt-1">Personel Listesi</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canExportXlsx && (
            <Button variant="outline" size="sm" onClick={exportPersonnelXlsx} data-testid="personnel-export-xlsx">
              <Download size={16} className="mr-1" />Excel İndir
            </Button>
          )}
          {canBulkSalary && (
            <Button variant="outline" size="sm" onClick={() => { setBulkSalaryOpen(true); setBulkSalaryResult(null); setBulkSalaryFile(null); }} data-testid="bulk-salary-btn">
              <UploadSimple size={16} className="mr-1" />Excel ile Maaş Güncelle
            </Button>
          )}
          {(["admin", "hr"].includes(realRole) || (realRole === "manager" && user?.can_view_dept_salaries)) && (
            <Button variant="outline" size="sm" onClick={() => setShowSalary((v) => !v)} data-testid="toggle-salary">
              {showSalary ? <EyeSlash size={16} className="mr-1" /> : <Eye size={16} className="mr-1" />}
              {showSalary ? "Maaşları Gizle" : "Maaşları Göster"}
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid md:grid-cols-4 gap-2">
            <div className="md:col-span-2 relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Ara: ad, e-posta, telefon, TC, departman, görev…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9"
                data-testid="personnel-search"
              />
            </div>
            <div>
              <Select value={filterDept} onValueChange={setFilterDept}>
                <SelectTrigger data-testid="personnel-filter-dept"><SelectValue placeholder="Departman" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Departmanlar</SelectItem>
                  {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-1">
              <Input type="date" value={hireFrom} onChange={(e) => setHireFrom(e.target.value)} placeholder="İşe giriş ≥" title="İşe giriş tarihi (başlangıç)" data-testid="personnel-hire-from" />
              <Input type="date" value={hireTo} onChange={(e) => setHireTo(e.target.value)} placeholder="İşe giriş ≤" title="İşe giriş tarihi (bitiş)" data-testid="personnel-hire-to" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span data-testid="personnel-count">Toplam: <b>{filteredList.length}</b> / {list.length} personel</span>
            {(q || filterDept !== "all" || hireFrom || hireTo) && (
              <Button variant="ghost" size="sm" onClick={() => { setQ(""); setFilterDept("all"); setHireFrom(""); setHireTo(""); }} data-testid="personnel-clear-filters">Filtreleri Temizle</Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-muted-foreground text-xs uppercase tracking-wider bg-muted/50">
                <tr>
                  <th className="text-left p-3">Ad Soyad</th>
                  <th className="text-left p-3">Departman</th>
                  <th className="text-left p-3">Görev</th>
                  <th className="text-left p-3">Telefon</th>
                  <th className="text-left p-3">Rol</th>
                  {showSalary && <th className="text-left p-3">{t("personnel.salary_col")}</th>}
                  <th className="text-left p-3">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="p-3 font-medium">
                      <div className="flex items-center gap-2">
                        <span>{p.first_name} {p.last_name}</span>
                        {isBirthdayToday(p.birth_date) && (
                          <span title="Bugün doğum günü!" data-testid={`birthday-icon-${p.id}`}>
                            <Cake size={16} className="text-pink-500 animate-bounce" weight="fill" />
                          </span>
                        )}
                        {p.is_active === false && <Badge variant="destructive" className="text-[9px]">{t("common.inactive")}</Badge>}
                      </div>
                    </td>
                    <td className="p-3">{p.department}</td>
                    <td className="p-3">{p.position}</td>
                    <td className="p-3">{p.phone}</td>
                    <td className="p-3"><Badge variant="secondary">{p.role}</Badge></td>
                    {showSalary && <td className="p-3">{p.salary != null ? `${Number(p.salary).toLocaleString("tr-TR")} ₺` : "—"}</td>}
                    <td className="p-3">
                      <div className="flex gap-1">
                        {canDownloadAllPayroll && (
                          <Button size="icon" variant="ghost" onClick={() => downloadAllPayrolls(p)} data-testid={`payroll-zip-${p.id}`} title="Tüm bordroları ZIP indir">
                            <Download size={16} />
                          </Button>
                        )}
                        {canDownloadAllPayroll && (
                          <Button size="icon" variant="ghost" onClick={() => downloadAllPayrollsPdf(p)} data-testid={`payroll-pdf-${p.id}`} title="Tüm imzalı bordroları tek PDF indir">
                            <FilePdf size={16} className="text-primary" />
                          </Button>
                        )}
                        {(canEdit || p.id === user?.id) && (
                          <Button size="icon" variant="ghost" onClick={() => setEdit({...p})} data-testid={`edit-${p.id}`}>
                            <PencilSimple size={16} />
                          </Button>
                        )}
                        {canDelete && p.id !== user?.id && (
                          <Button size="icon" variant="ghost" onClick={() => del(p)} data-testid={`delete-${p.id}`}>
                            <Trash size={16} className="text-destructive" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {list.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">{t("common.no_records")}</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="delete-personnel-dialog">
          <DialogHeader>
            <DialogTitle>Personeli Sil — Ayrılma Nedeni</DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-4">
              <div className="rounded-md bg-muted/40 p-3 text-sm">
                <b>{deleteTarget.first_name} {deleteTarget.last_name}</b>
                <span className="text-muted-foreground"> · {deleteTarget.department || "-"} · {deleteTarget.position || "-"}</span>
              </div>
              <div>
                <Label>Ayrılma Nedeni <span className="text-destructive">*</span></Label>
                <Select value={depReason} onValueChange={setDepReason}>
                  <SelectTrigger data-testid="departure-reason-select"><SelectValue placeholder="Neden seçiniz" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resignation">İstifa</SelectItem>
                    <SelectItem value="termination">İşten Çıkarma</SelectItem>
                    <SelectItem value="retirement">Emeklilik</SelectItem>
                    <SelectItem value="other">Diğer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ayrılma Tarihi</Label>
                <Input type="date" value={depDate} onChange={(e) => setDepDate(e.target.value)} data-testid="departure-date-input" />
              </div>
              <div>
                <Label>Not (opsiyonel)</Label>
                <Input value={depNote} onChange={(e) => setDepNote(e.target.value)} placeholder="Açıklama" data-testid="departure-note-input" />
              </div>
              <div className="text-xs text-muted-foreground">
                Bu işlem personeli kalıcı olarak siler; ayrılma nedeni ve tarihi devir oranı raporunda kullanılmak üzere kaydedilir.
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="cancel-delete-btn">Vazgeç</Button>
                <Button variant="destructive" onClick={confirmDelete} disabled={!depReason || deleting} data-testid="confirm-delete-btn">
                  {deleting ? "Siliniyor…" : "Sil"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t("personnel.edit_title")}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Ad</Label><Input value={edit.first_name || ""} onChange={(e) => setEdit({...edit, first_name: e.target.value})} /></div>
              <div><Label>Soyad</Label><Input value={edit.last_name || ""} onChange={(e) => setEdit({...edit, last_name: e.target.value})} /></div>
              <div><Label>Telefon</Label><Input value={edit.phone || ""} onChange={(e) => setEdit({...edit, phone: e.target.value})} /></div>
              <div><Label>T.C. Kimlik</Label><Input value={edit.tc_kimlik || ""} onChange={(e) => setEdit({...edit, tc_kimlik: e.target.value})} /></div>
              <div><Label>Departman</Label><Input value={edit.department || ""} onChange={(e) => setEdit({...edit, department: e.target.value})} /></div>
              <div><Label>Görev</Label><Input value={edit.position || ""} onChange={(e) => setEdit({...edit, position: e.target.value})} /></div>
              <div>
                <Label>Cinsiyet</Label>
                <Select value={edit.gender || ""} onValueChange={(v) => setEdit({...edit, gender: v})}>
                  <SelectTrigger data-testid="edit-gender"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Erkek</SelectItem>
                    <SelectItem value="F">Kadın</SelectItem>
                    <SelectItem value="O">Belirtmek istemiyorum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Doğum Tarihi</Label><Input type="date" value={(edit.birth_date || "").slice(0,10)} onChange={(e) => setEdit({...edit, birth_date: e.target.value})} /></div>
              <div className="md:col-span-2"><Label>Adres</Label><Input value={edit.address || ""} onChange={(e) => setEdit({...edit, address: e.target.value})} /></div>
              {canEdit && (
                <>
                  <div>
                    <Label>Rol</Label>
                    <Select value={edit.role} onValueChange={(v) => setEdit({...edit, role: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="staff">Personel</SelectItem>
                        <SelectItem value="manager">Müdür</SelectItem>
                        <SelectItem value="gm">Genel Müdür</SelectItem>
                        <SelectItem value="hr">İK Sorumlusu</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Maaş (₺)</Label><Input type="number" value={edit.salary || 0} onChange={(e) => setEdit({...edit, salary: e.target.value})} /></div>
                  <div className="flex items-center gap-2"><Switch checked={!!edit.can_view_dept_salaries} onCheckedChange={(v) => setEdit({...edit, can_view_dept_salaries: v})} /><Label>Dep. maaş görüntüleme yetkisi</Label></div>
                  <div className="flex items-center gap-2"><Switch checked={!!edit.annual_leave_board} onCheckedChange={(v) => setEdit({...edit, annual_leave_board: v})} /><Label>Yıllık İzin Kurulu üyesi</Label></div>
                  <div><Label>İşe Giriş Tarihi</Label><Input type="date" value={(edit.hire_date || "").slice(0,10)} onChange={(e) => setEdit({...edit, hire_date: e.target.value})} /></div>
                  <div><Label>İşten Çıkış Tarihi</Label><Input type="date" value={(edit.exit_date || "").slice(0,10)} onChange={(e) => setEdit({...edit, exit_date: e.target.value})} /></div>
                  <div>
                    <Label>Yıllık İzin - Devir (gün)</Label>
                    <Input type="number" step="0.5" value={edit.annual_leave_carryover ?? ""} placeholder="Geçmiş yıldan kalan gün"
                           onChange={(e) => setEdit({...edit, annual_leave_carryover: e.target.value === "" ? null : Number(e.target.value)})}
                           data-testid="edit-carryover" />
                    <div className="text-[10px] text-muted-foreground mt-1">Hak edilene eklenir.</div>
                  </div>
                  <div>
                    <Label>Yıllık İzin - Manuel Kalan (gün)</Label>
                    <Input type="number" step="0.5" value={edit.annual_leave_manual_override ?? ""} placeholder="Boş bırak = otomatik"
                           onChange={(e) => setEdit({...edit, annual_leave_manual_override: e.target.value === "" ? null : Number(e.target.value)})}
                           data-testid="edit-override" />
                    <div className="text-[10px] text-muted-foreground mt-1">Boş = kıdem/yaş kuralına göre otomatik. Doldurulursa bu yıl için baz izin bu değerdir.</div>
                  </div>
                  <div className="md:col-span-2 rounded-md border border-primary/40 bg-primary/5 p-3 space-y-1" data-testid="annual-summary">
                    <div className="text-xs uppercase tracking-wider text-primary font-semibold">Yıllık İzin Özeti (canlı)</div>
                    {(() => {
                      const carry = Number(edit.annual_leave_carryover || 0);
                      const manual = edit.annual_leave_manual_override;
                      const auto = (() => {
                        // basit tahmini hesap; net değer backend'den gelir
                        try {
                          const hire = new Date(edit.hire_date);
                          const birth = new Date(edit.birth_date);
                          const now = new Date();
                          const tenure = (now - hire) / (365.25 * 86400000);
                          const age = (now - birth) / (365.25 * 86400000);
                          if (age >= 50 || age < 18) return tenure >= 15 ? 26 : 21;
                          if (tenure < 1) return 14;
                          if (tenure < 5) return 14;
                          if (tenure < 15) return 21;
                          return 26;
                        } catch { return 14; }
                      })();
                      const base = manual != null && manual !== "" ? Number(manual) : auto;
                      const total = base + carry;
                      return (
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          <div>Otomatik hak (kıdem+yaş): <b>{auto} gün</b></div>
                          <div>Bu yıl baz (manuel varsa manuel): <b>{base} gün</b></div>
                          <div>Devir (geçmişten kalan): <b>{carry} gün</b></div>
                          <div className="text-primary">Toplam yıllık izin: <b>{total} gün</b></div>
                        </div>
                      );
                    })()}
                    <div className="text-[10px] text-muted-foreground">Onaylanan yıllık izinler bu toplamdan otomatik düşer.</div>
                  </div>
                  <div className="md:col-span-2 flex items-center justify-between gap-2 p-3 rounded-md border border-border bg-muted/30">
                    <div>
                      <div className="text-sm font-semibold">Aktif / Pasif</div>
                      <div className="text-xs text-muted-foreground">Pasif alınan personel sisteme giriş yapamaz, turnover&apos;a dahil edilir.</div>
                    </div>
                    <Switch checked={edit.is_active !== false} onCheckedChange={(v) => setEdit({...edit, is_active: v})} data-testid="edit-active" />
                  </div>
                </>
              )}
              <div className="flex items-center gap-2 md:col-span-2"><Switch checked={!!edit.two_factor_enabled} onCheckedChange={(v) => setEdit({...edit, two_factor_enabled: v})} /><Label>SMS ile İki Aşamalı Doğrulama</Label></div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEdit(null)}>İptal</Button>
                <Button onClick={save} data-testid="edit-save">Kaydet</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={bulkSalaryOpen} onOpenChange={setBulkSalaryOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Toplu Maaş Güncelleme</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Excel dosyanızda şu kolonlar bulunmalı: <span className="font-semibold">Ad Soyad</span> (veya ayrı <span className="font-semibold">Ad</span> + <span className="font-semibold">Soyad</span>) ve <span className="font-semibold">Maas</span>.
              İsteğe bağlı: <span className="font-mono text-xs">TC</span> veya <span className="font-mono text-xs">Email</span> (daha doğru eşleşme için).
            </div>
            <div className="rounded-md bg-muted/40 p-3 text-xs font-mono">
              <div>Ad Soyad | Maas</div>
              <div className="text-muted-foreground">Ahmet Yılmaz | 45000</div>
              <div className="text-muted-foreground">Ayşe Demir | 52000</div>
            </div>
            <div>
              <Label>Excel Dosyası (.xlsx)</Label>
              <Input type="file" accept=".xlsx,.xls" onChange={(e) => setBulkSalaryFile(e.target.files?.[0])} data-testid="bulk-salary-file" />
            </div>
            <Button onClick={doBulkSalaryUpload} disabled={uploadingSalary || !bulkSalaryFile} data-testid="bulk-salary-submit">
              {uploadingSalary ? "Yükleniyor…" : "Maaşları Güncelle"}
            </Button>
            {bulkSalaryResult && (
              <div className="text-xs bg-muted p-3 rounded space-y-1">
                <div><span className="font-semibold text-primary">{bulkSalaryResult.updated}</span> personel güncellendi.</div>
                {bulkSalaryResult.skipped > 0 && <div className="text-muted-foreground">{bulkSalaryResult.skipped} satır atlandı (eksik / hatalı maaş).</div>}
                {bulkSalaryResult.not_found?.length > 0 && (
                  <div>
                    <div className="text-destructive font-semibold">Eşleşmeyen ({bulkSalaryResult.not_found.length}):</div>
                    <div className="text-muted-foreground pl-2">{bulkSalaryResult.not_found.join(", ")}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
